import { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import { lazyWithRetry } from './lib/lazy-with-retry';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient, tsr, AuthProvider, CartProvider, AnalyticsProvider, RouteTracker, type AnalyticsConfig } from '@flote/app-core';
import { Toaster } from 'sonner';
import { ModalProvider } from './lib/modal';
import { Layout } from './components/layout';
import { ModalRenderer } from './components/modals/ModalRenderer';
import { CartDrawer } from './components/cart';
import { SentryErrorBoundary } from '@flote/sentry-browser';
import { ErrorFallback } from './components/ErrorBoundary';
import { SentryAuthSync } from './components/SentryAuthSync';
import { ScrollToTop } from './components/ScrollToTop';
import { useGlobalErrorHandler } from './lib/hooks';
import { AgeVerificationProvider } from './lib/age-verification';
import { AgeGateDialog } from './components/AgeGateDialog';
import { Button } from '@flote/ui';

// Route-level code splitting
const BrandsPage = lazyWithRetry(() => import('./pages/brands/BrandsPage').then(m => ({ default: m.BrandsPage })));
const BrandProductsPage = lazyWithRetry(() => import('./pages/brands/brand-products/BrandsProductsPage').then(m => ({ default: m.BrandProductsPage })));
const AllProductsPage = lazyWithRetry(() => import('./pages/products').then(m => ({ default: m.AllProductsPage })));
const CheckoutPage = lazyWithRetry(() => import('./pages/checkout').then(m => ({ default: m.CheckoutPage })));
const OrderConfirmationPage = lazyWithRetry(() => import('./pages/OrderConfirmationPage').then(m => ({ default: m.OrderConfirmationPage })));
const TermsPage = lazyWithRetry(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const OrdersPage = lazyWithRetry(() => import('./pages/orders').then(m => ({ default: m.OrdersPage })));
const OrderDetailPage = lazyWithRetry(() => import('./pages/orders').then(m => ({ default: m.OrderDetailPage })));
const UnsubscribeBackInStockPage = lazyWithRetry(() => import('./pages/UnsubscribeBackInStockPage').then(m => ({ default: m.UnsubscribeBackInStockPage })));

/** Sets up global error handling for API mutations */
function GlobalErrorHandler({ children }: { children: React.ReactNode }) {
  useGlobalErrorHandler();
  return <>{children}</>;
}

function NotFoundPage() {
  return (
    <div className="container mx-auto flex min-h-[60vh] flex-col items-center justify-center px-4 py-16">
      <h1 className="text-6xl font-bold text-primary">404</h1>
      <p className="mt-4 text-xl text-muted-foreground">Page not found</p>
      <Button asChild className="mt-6">
        <Link to="/products">Browse Products</Link>
      </Button>
    </div>
  );
}

function MaintenancePage() {
  const message = import.meta.env.VITE_MAINTENANCE_MESSAGE
    || "We're currently experiencing issues with one of our service providers. Please check back shortly.";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-flote-gray-50 px-4">
      <div className="flex max-w-md flex-col items-center text-center">
        <img src="/Flote_Word_Red.png" alt="Flote" className="mb-10 h-8" />
        <div className="h-1 w-12 rounded-full bg-flote-red-500 mb-8" />
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-flote-gray-800">
          We'll Be Right Back
        </h1>
        <p className="mt-3 font-body text-flote-gray-500 leading-relaxed">
          {message}
        </p>
      </div>
    </div>
  );
}

export function App() {
  if (import.meta.env.VITE_MAINTENANCE_MODE === 'true') {
    return <MaintenancePage />;
  }

  // Analytics configuration for PostHog
  const analyticsConfig: AnalyticsConfig = useMemo(() => ({
    apiKey: import.meta.env.VITE_POSTHOG_KEY || '',
    appSource: 'storefront',
    apiHost: import.meta.env.VITE_POSTHOG_HOST,
    autocapture: true, // Enabled for standard SPA
    capturePageview: true, // Auto track for BrowserRouter
    sessionReplay: true, // Enable session recording for user journey analysis
    disabled: !import.meta.env.VITE_POSTHOG_KEY,
    debug: import.meta.env.DEV,
  }), []);

  return (
    <QueryClientProvider client={queryClient}>
      <tsr.ReactQueryProvider>
        <GlobalErrorHandler>
          <AnalyticsProvider config={analyticsConfig}>
            <BrowserRouter>
              <RouteTracker />
              <ScrollToTop />
              <AgeVerificationProvider>
                <AgeGateDialog />
                <SentryErrorBoundary fallback={ErrorFallback}>
                  <AuthProvider>
                    <SentryAuthSync />
                    <CartProvider>
                      <ModalProvider>
                        <Routes>
                          <Route element={<Layout />}>
                            <Route path="/" element={<Navigate to="/brands" replace />} />
                            <Route path="/brands" element={<BrandsPage />} />
                            <Route path="/orders" element={<OrdersPage />} />
                            <Route path="/orders/:orderId" element={<OrderDetailPage />} />
                            <Route path="/brands/:brandId/products" element={<BrandProductsPage />} />
                            <Route path="/products" element={<AllProductsPage />} />
                            <Route path="/checkout" element={<CheckoutPage />} />
                            <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                            <Route path="/notifications/unsubscribe" element={<UnsubscribeBackInStockPage />} />
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="*" element={<NotFoundPage />} />
                          </Route>
                        </Routes>
                        <ModalRenderer />
                        <CartDrawer />
                        <Toaster position="top-center" richColors />
                      </ModalProvider>
                    </CartProvider>
                  </AuthProvider>
                </SentryErrorBoundary>
              </AgeVerificationProvider>
            </BrowserRouter>
          </AnalyticsProvider>
        </GlobalErrorHandler>
      </tsr.ReactQueryProvider>
    </QueryClientProvider>
  );
}
